import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Circle, MessageSquare, Pencil, Send, Award } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { TRAIN } from "@/constants/testIds";

export default function ModulePage() {
  const { trackKey, n } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [qBody, setQBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [submittingQ, setSubmittingQ] = useState(false);

  const reload = async () => {
    setErr("");
    try {
      // Find module by track + number
      const { data: tdata } = await api.get(`/training/tracks/${trackKey}`);
      const target = tdata.modules.find((m) => m.number === parseInt(n, 10));
      if (!target) { setErr(`Module ${n} not found in this track.`); return; }
      const { data: mdata } = await api.get(`/training/modules/${target.id}`);
      setData({ ...mdata, trackTitle: tdata.track.title, totalModules: tdata.modules.length });
      setNoteBody(mdata.note?.body || "");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-line */ }, [trackKey, n]);

  if (err) return <div className="text-rose-300">{err} <Link to={`/training/${trackKey}`} className="text-lifted-green underline ml-2">Back to track</Link></div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>;

  const m = data.module;
  const prog = data.progress;
  const completedSet = new Set(prog?.completed_subcategories || []);

  const toggleSubcat = async (sid) => {
    if (completedSet.has(sid)) return;  // forward-only
    await api.post(`/training/modules/${m.id}/progress`, { subcategory_id: sid });
    await reload();
  };
  const markComplete = async () => {
    await api.post(`/training/modules/${m.id}/progress`, { completed: true });
    await reload();
  };
  const saveNote = async () => {
    setSavingNote(true);
    try { await api.post("/training/notes", { module_id: m.id, body: noteBody }); await reload(); }
    finally { setSavingNote(false); }
  };
  const askQuestion = async () => {
    if (!qBody.trim()) return;
    setSubmittingQ(true);
    try {
      await api.post("/training/questions", { module_id: m.id, body: qBody.trim() });
      setQBody(""); await reload();
    } finally { setSubmittingQ(false); }
  };

  return (
    <div data-testid={TRAIN.modulePage} className="space-y-8 max-w-5xl">
      <button onClick={() => navigate(`/training/${trackKey}`)} className="btn-ghost !text-[0.7rem]">
        <ArrowLeft className="h-3 w-3" /> Back to {data.trackTitle}
      </button>

      <header className="fade-up">
        <div className="eyebrow">Module {m.number} of {data.totalModules}</div>
        <h1 data-testid={TRAIN.moduleTitle} className="headline-xl">{m.title}</h1>
        <p className="text-lifted-dim mt-3 max-w-3xl">{m.description}</p>
      </header>

      <section className="glass-panel p-6 space-y-4">
        <div className="eyebrow !text-lifted-text-dim">Content</div>
        <div className="prose prose-invert max-w-none text-lifted-text leading-relaxed whitespace-pre-line">{m.content}</div>
      </section>

      <section className="glass-panel p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div className="eyebrow !text-lifted-text-dim">Sub-modules</div>
          <span className="font-bebas tracking-[0.2em] text-xs text-lifted-green">
            {completedSet.size} / {m.subcategories?.length || 0} done · {prog?.completion_pct || 0}%
          </span>
        </div>
        <ul className="space-y-2">
          {(m.subcategories || []).map((s) => {
            const done = completedSet.has(s.id);
            return (
              <li key={s.id}>
                <button
                  data-testid={TRAIN.subcategoryCheckbox(s.id)}
                  onClick={() => toggleSubcat(s.id)}
                  disabled={done}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                    done
                      ? "border-lifted-green/30 bg-lifted-green/5 text-lifted-text"
                      : "border-white/10 hover:border-lifted-green/30 hover:bg-white/[0.03] text-lifted-text"
                  }`}
                >
                  {done ? <Check className="h-4 w-4 text-lifted-green" /> : <Circle className="h-4 w-4 text-lifted-mute" />}
                  <span className="font-bebas tracking-[0.14em] text-sm">{s.title}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="pt-2">
          <button
            data-testid={TRAIN.completeModuleBtn}
            onClick={markComplete}
            disabled={prog?.completed}
            className="btn-lifted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {prog?.completed ? "Module completed" : "Mark module complete"}
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Notes */}
        <div className="glass-panel p-6">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Pencil className="h-3 w-3" /> Your notes</div>
          <textarea
            data-testid={TRAIN.noteTextarea}
            className="input-lifted mt-3 min-h-[140px] resize-y"
            value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
            placeholder="Capture key takeaways…"
          />
          <button data-testid={TRAIN.noteSave} onClick={saveNote} disabled={savingNote} className="btn-lifted mt-3 !text-[0.7rem] !px-4 !py-2">
            {savingNote ? "Saving…" : "Save note"}
          </button>
        </div>

        {/* Questions */}
        <div className="glass-panel p-6">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Ask a question</div>
          <div className="mt-3 flex gap-2">
            <input
              data-testid={TRAIN.questionInput}
              className="input-lifted flex-1"
              value={qBody} onChange={(e) => setQBody(e.target.value)}
              placeholder="Ask your trainer or a teacher…"
            />
            <button data-testid={TRAIN.questionSubmit} onClick={askQuestion} disabled={submittingQ || !qBody.trim()} className="btn-lifted !px-4 !py-2 !text-[0.7rem]">
              <Send className="h-3 w-3" />
            </button>
          </div>
          <ul className="mt-4 space-y-2">
            {(data.questions || []).map((q) => (
              <li key={q.id} data-testid={TRAIN.questionItem(q.id)} className="text-sm border-l-2 pl-3 py-1 border-lifted-green/30">
                <div className="text-lifted-text">{q.body}</div>
                {q.answer
                  ? <div className="text-xs text-lifted-green mt-1">↳ {q.answer} <span className="text-lifted-mute">— {q.answered_by_name}</span></div>
                  : <div className="text-xs text-lifted-mute mt-1">Awaiting answer…</div>}
              </li>
            ))}
            {(!data.questions || data.questions.length === 0) && (
              <li className="text-xs text-lifted-mute">No questions yet.</li>
            )}
          </ul>
        </div>
      </section>

      {prog?.completed && (
        <section className="glass-panel p-6 border-lifted-green/30">
          <div className="flex items-center gap-3">
            <Award className="h-5 w-5 text-lifted-green" />
            <div>
              <div className="font-anton uppercase">Nice work.</div>
              <div className="text-sm text-lifted-dim">Continue to the next module or sit the certification once all modules are complete.</div>
            </div>
            <Link to={`/training/${trackKey}`} className="btn-ghost ml-auto">Back to track</Link>
          </div>
        </section>
      )}
    </div>
  );
}
